import React, { useEffect, useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { FormContext } from "@features/port-security/components/SecurityClearence.types.ts";
import { buildCountryOptions } from "@features/port-security/components/utils";
import type { PortSecurityFormData } from "@features/port-security/schema/portSecurityFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { stringToTitleCase } from "@utils/formatters/strings";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

type Country = {
  countryName: string;
  countryCode: string;
};

const MILITARY_FIELD_NAMES: Array<
  | "countryName"
  | "serviceType"
  | "rank"
  | "durationOfService"
  | "militaryServiceStartDate"
  | "militaryServiceEndDate"
> = [
  "countryName",
  "serviceType",
  "rank",
  "durationOfService",
  "militaryServiceStartDate",
  "militaryServiceEndDate",
];

const MilitaryField: React.FC = () => {
  const radioOptions = [
    { label: t("common.yes"), value: true },
    { label: t("common.no"), value: false },
  ];
  const { formData, updateFormData, validateAllStepsStatuses } = useOutletContext<FormContext>();
  const hasServed = formData.getValues("hasServed");

  const handleHasServedChange = (key: "hasServed", value: PortSecurityFormData["hasServed"]) => {
    updateFormData(key, value);
    if (value === true) {
      void formData.trigger();
      validateAllStepsStatuses?.();
      return;
    }
    formData.clearErrors(MILITARY_FIELD_NAMES);
  };

  useEffect(() => {
    if (hasServed === true) {
      void formData.trigger();
      validateAllStepsStatuses?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { options: countries } = useFormattedReference<Country>(
    REFERENCE_DATA_TYPES.COUNTRIES,
    "bff-gatepass",
  );

  const countryOptions = useMemo(
    () => buildCountryOptions(countries, { valueMode: "code", formatName: stringToTitleCase }),
    [countries],
  );
  return (
    <FormProvider {...formData}>
      <FormSection title={t("security-clearance.military-service")}>
        <div className="col-span-2">
          <FormItem<PortSecurityFormData, "hasServed">
            name="hasServed"
            type="radio"
            options={radioOptions}
            label={t("security-clearance.military-service-question")}
            testId="security-military-service"
            onFieldChange={handleHasServedChange}
            horizontalRadioButtons
          />
        </div>
        {hasServed && (
          <>
            <FormItem<PortSecurityFormData, "countryName">
              name="countryName"
              type="autocomplete"
              options={countryOptions}
              label={t("security-clearance.country-name")}
              testId="security-countryName"
              onFieldChange={updateFormData}
              required
            />
            <FormItem<PortSecurityFormData, "serviceType">
              name="serviceType"
              type="text"
              maxChars={200}
              label={t("security-clearance.service-type")}
              testId="security-serviceType"
              onFieldChange={updateFormData}
              required
            />
            <FormItem<PortSecurityFormData, "rank">
              name="rank"
              type="text"
              maxChars={200}
              label={t("security-clearance.rank")}
              testId="security-rank"
              onFieldChange={updateFormData}
              required
            />
            <FormItem<PortSecurityFormData, "durationOfService">
              name="durationOfService"
              type="text"
              maxChars={200}
              label={t("security-clearance.service-duration")}
              testId="security-durationOfService"
              onFieldChange={updateFormData}
              required
            />
            <FormItem<PortSecurityFormData, "militaryServiceStartDate">
              name="militaryServiceStartDate"
              type="date-picker"
              label={t("security-clearance.military-service-start-date")}
              testId="security-militaryServiceStartDate"
              onFieldChange={updateFormData}
              timePicker={false}
              required
              dateConstraint="past"
            />
            <FormItem<PortSecurityFormData, "militaryServiceEndDate">
              name="militaryServiceEndDate"
              type="date-picker"
              label={t("security-clearance.military-service-end-date")}
              testId="security-militaryServiceEndDate"
              onFieldChange={updateFormData}
              timePicker={false}
            />
          </>
        )}
      </FormSection>
    </FormProvider>
  );
};

export default MilitaryField;
